/**
 * SurveyQuestionsPreview Component
 *
 * Displays AI-parsed survey questions in editable form for user review and modification
 * Specifically designed for the human survey feature
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Save, AlertCircle, CheckCircle, ArrowLeft, Lock } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT } from '../../config/ui-text';
import { PANEL_PROVIDERS } from '../../config/panel-providers';
import {
  isExternalStreamingVideoUrl,
  getYouTubeThumbnail,
  getEmbedUrl,
} from '../../components/survey/MediaUploadGrid';
import {
  surveyIdToShareId,
  updateSurveyQuestions,
  regenerateDynamicSectionRubric,
  keepDynamicSectionRubric,
  type SurveyBlockDefinition,
  type SurveyStyle,
  type DiscussionGuide,
  type RapidFireConfig,
} from '../../utils/surveyApi';
import type { ChoiceAction, ParsedQuestion } from '../../components/survey/types';
import type { SurveyTimerConfig } from './surveyTimer';
import {
  EMPTY_TIMER_FORM,
  MIN_DURATION_MINUTES,
  MAX_DURATION_MINUTES,
  durationWarning,
  timerConfigFromForm,
  timerFormFromConfig,
  type TimerFormState,
} from './surveyTimerAuthoring';
import {
  noSettingsTouched,
  unsupportedReason,
  lockedReason,
  type SurveyAuthoringSettingKey,
  type SurveyAuthoringSettings,
  type SurveyAuthoringSurface,
} from './surveyAuthoringSettings';
import { MULTI_SELECT_TYPE, selectionCap } from './multiSelect';
import DiscussionGuideEditor from './DiscussionGuideEditor';
import type { ValidationIssue } from '../../utils/surveyValidation';
import QuestionCard from './QuestionCard';
import {
  CHOICE_INDEXED_MEDIA_TYPES,
  DEFAULT_RAPID_FIRE_CONFIG,
  QUESTION_TYPES,
  maxSelectableOptions,
} from './questionCardHelpers';

/**
 * Part of this module's public surface, which is why it is re-exported rather than left where it
 * now lives: the declarations moved to `questionCardHelpers` only so QuestionCard could share them.
 */
/* eslint-disable react-refresh/only-export-components */
export {
  blockAccentClass,
  formatRandomizationPoolLabel,
  nextRandomizationPoolId,
  randomizationPoolBadgeClass,
  shouldRandomizeMedia,
  tierLabel,
} from './questionCardHelpers';
/* eslint-enable react-refresh/only-export-components */

export interface RedirectConfig {
  redirect_base_url: string;
  screened_out_url?: string | null; // Optional: separate URL for screened out participants
  tracking_param: { [key: string]: string };
  completion_params: { [key: string]: string } | null;
  screen_out_params: { [key: string]: string } | null;
  quota_full_params: { [key: string]: string } | null;
}

interface SurveyQuestionsPreviewProps {
  parsedQuestions: ParsedQuestion[];
  surveyName: string;
  /** When set, open that question in the editor (used by the pre-save validation modal's
   * "Go to question"). Cleared via onFocusQuestionHandled once applied. */
  focusQuestionId?: string | null;
  onFocusQuestionHandled?: () => void;
  /** Per-question structural issues from the validator; renders an inline X/warning badge. */
  issuesByQuestion?: Map<string, ValidationIssue[]>;
  /**
   * Both save callbacks carry the SAME named settings object (spec 42, #701).
   *
   * They used to be positional and asymmetric — nine arguments here, seven there, three of the
   * nine with no create-path equivalent — and a positional callback lets a parent ignore argument
   * seven in silence. One named type in both directions is what makes a dropped setting
   * impossible to write by accident and visible to G2 when it happens anyway.
   */
  onQuestionsUpdated: (questions: ParsedQuestion[], settings: SurveyAuthoringSettings) => void;
  onCreateSurvey: (
    surveyName: string,
    questions: ParsedQuestion[],
    settings: SurveyAuthoringSettings,
  ) => Promise<void>;
  /**
   * Which authoring surface this editor is mounted on — it decides which settings can be
   * persisted, and therefore which controls may be offered at all.
   *
   * Defaults to `survey_edit`, the surface with no restrictions: a caller that forgets the prop
   * gets every control rather than silently losing one.
   */
  surface?: SurveyAuthoringSurface;
  onGoBack: () => void;
  isLoading?: boolean;
  className?: string;
  readOnly?: boolean;
  hideActionButtons?: boolean;
  initialMaxResponses?: number | null;
  initialAdaptiveProbing?: boolean;
  initialSurveyLevelProbing?: boolean;
  /** Experimental: conversational / discussion-guide style + guide, loaded from the survey. */
  initialSurveyStyle?: SurveyStyle;
  initialDiscussionGuide?: DiscussionGuide | null;
  initialMinLinkedInConnections?: number | null;
  /** The survey's stored timer_config, or null/undefined when it is not on a clock. */
  initialTimerConfig?: SurveyTimerConfig | null;
  initialRedirectConfig?: RedirectConfig | null;
  /** Block definitions from survey API; omit to keep local block state only (e.g. new survey builder). */
  initialBlocks?: SurveyBlockDefinition[] | null;
  groupCount?: number;
  surveyId?: number | null;
  /** For group surveys: all child survey IDs (parallel to groupLabels) */
  groupSurveyIds?: number[];
  /** For group surveys: labels for each group */
  groupLabels?: string[];
}

// Question types that support answer choices (mutually exclusive with scale-based types)
const CHOICE_BASED_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'ranking'];

// Question types that support scale settings
const SCALE_BASED_TYPES = ['rating_scale', 'likert_scale', 'nps'];

// Question types that support ranking settings
const RANKING_BASED_TYPES = ['ranking'];

/**
 * Said out loud whenever the field holds a number bigger than the question can satisfy.
 *
 * The field shows what the author TYPED and the question stores what fits (see
 * `editorSelectionCap`). Those two differing silently is exactly the defect this closes, so the
 * difference is never left to be inferred from a field that refused to move.
 */
const MAX_SELECTIONS_BOUNDED_NOTE = (limit: number) =>
  `This question offers ${limit} option${limit === 1 ? '' : 's'}, so ${limit} is what respondents ` +
  `get until you add more.`;

/**
 * Said out loud when the survey ARRIVED carrying a cap the runtime ignores (a stale 0 from an
 * older AI parse). `normalizeSelectionCap` clears it in the editor's copy, but the copy the
 * server holds — and validates activation against — still has it until something saves.
 */
const MAX_SELECTIONS_REPAIRED_NOTE = (stored: unknown) =>
  `A stored limit of ${JSON.stringify(stored)} cannot be enforced, so nothing caps this question ` +
  `today. Save the survey to clear it — activation stays blocked until you do.`;

/**
 * The cap a question should carry now its option count has moved, or undefined when nothing needs
 * to change.
 *
 * The cap is stored bounded, so it has to be re-derived whenever the bound moves. Removing a
 * choice and switching "Other" off both shrink the option count: 4 choices + Other with a cap of 5
 * (accepted), Other switched off, and the survey now stores 5 against 4 selectable options. The
 * runtime honours it — the counter reads "Selected: 1 of 5" and nothing ever locks — while the
 * publish validator rejects `max_selections_exceeds_choices`, so activation is blocked by a value
 * the editor presents as perfectly valid.
 *
 * Round 7: it goes UP as well as down, because `requested` is the number the author asked for
 * rather than the number that survived the last bound. Lowering only was a one-way ratchet — a
 * cap typed while the question still held its two placeholder choices was reduced to 2 and stayed
 * at 2 however many options followed, which is the ordinary authoring order. The result is always
 * `min(requested, options)`, so the invariant the lowering existed for is untouched: what is
 * STORED still always fits. Called without a `requested` it is exactly the old ratchet.
 *
 * A question with no selectable options at all is left alone: `empty_choices` already reports that
 * same missing thing, and clearing the author's number over it would just lose their intent.
 */
const reclampedSelectionCap = (
  question: ParsedQuestion,
  requested?: number,
): number | undefined => {
  const cap = selectionCap(question);
  const limit = maxSelectableOptions(question);
  if (cap === null || limit === undefined) return undefined;
  const next = Math.min(requested ?? cap, limit);
  return next === cap ? undefined : next;
};

/**
 * Drop a stored cap the runtime would ignore, once, as the survey loads.
 *
 * Both the editor and the read-only summary render through `selectionCap` — the runtime predicate,
 * which discards 0, negatives and fractionals — but nothing rewrote the stored field, because
 * `updateQuestion` fires only on `onChange`. So an older AI parse's `max_selections: 0` was shown
 * to the author as "No limit", never touched, saved, and passed straight back to the backend where
 * the publish validator rejects it: blocked at activation by a value the UI said was absent.
 *
 * Only ever a removal, and only of a value that already has no effect — never an invented one. A
 * question with no cap keeps no key.
 *
 * Scoped to the ONE type whose editor shows the cap, which `selectionCap` alone is not: it answers
 * null for a `ranking` carrying a leftover `max_selections` exactly as it does for a stale 0. That
 * reach cost more than it bought once the repair started marking the survey dirty (see the load
 * effect): an unexplained "Unsaved changes" on a survey nobody had touched — `MAX_SELECTIONS_
 * REPAIRED_NOTE` renders inside the multi-select block only — with the re-sync effect frozen behind
 * the same flag, so polling stopped until an unrelated question was saved.
 *
 * And the note it could not show would have been untrue there. It says "activation stays blocked
 * until you do"; off-type the server raises `max_selections_wrong_type` as a WARNING with a
 * `clear_max_selections` auto-fix, deliberately, because `question_parsing_tasks` copies the field
 * onto every parsed type and live surveys already carry one. Nothing is blocked, so there is no
 * dead end here to close — the value goes back exactly as it arrived and the server offers the fix.
 *
 * Returns the SAME object when there is nothing to repair, so the caller can tell a repaired
 * question from an untouched one by identity and does not have to re-run the predicate.
 */
const normalizeSelectionCap = (question: ParsedQuestion): ParsedQuestion =>
  question.type !== MULTI_SELECT_TYPE ||
  question.max_selections == null ||
  selectionCap(question) !== null
    ? question
    : { ...question, max_selections: null };

/** Whether two id-keyed maps describe the same set of questions — used to skip a no-op setState. */
const sameKeys = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => k in b);
};

// Question types that support allocation settings
const ALLOCATION_BASED_TYPES = ['percentage_allocation'];

// Question types that support matrix settings
const MATRIX_BASED_TYPES = ['matrix_rating'];

// Question types that support MaxDiff settings — which is where a best/worst question gets its
// claims list AND its per-claim `option_media` sidecar, the pair the runtime actually reads
// (ADR-0003 §3). Both of its modes are `max_diff`, so one entry covers them.
const MAX_DIFF_BASED_TYPES = ['max_diff'];

// Question types that support Conjoint Analysis settings
const CONJOINT_BASED_TYPES = ['conjoint_analysis'];

// Question types that support Image Hotspot settings
const IMAGE_HOTSPOT_TYPES = ['image_hotspot'];

// Question types that support Photo Upload settings
const PHOTO_UPLOAD_TYPES = ['photo_upload'];

/**
 * The survey content the parent handed over, as one comparable value.
 *
 * The re-sync gates on this, not on prop identity: a save hands back a brand-new object every time
 * whether anything changed or not, and rebuilding is what moves the author. `updated_at` is no use
 * — it moves on writes that changed nothing. Computed on the PROCESSED questions so the editor's
 * own normalisation is not reported as a difference. Fails safe one way only: a spurious difference
 * costs a rebuild, a spurious match would show what the database does not hold (#661).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function questionsRevision(
  questions: readonly ParsedQuestion[],
  blocks: readonly SurveyBlockDefinition[] | null | undefined,
): string {
  return JSON.stringify([questions, blocks ?? null]);
}

/**
 * Surfaces where `isLoading` may replace the editor with a spinner.
 *
 * Only `create`, where the screen is about to be replaced anyway. On the EDIT surfaces a save is in
 * flight and swapping the pane destroys the DOM the author is standing on — measured before this
 * fix, one keystroke into a block label left focus on `<body>` and scroll at 0.
 */
const SURFACES_THAT_MAY_HIDE_THE_EDITOR: readonly SurveyAuthoringSurface[] = ['create'];

// What the "Survey-level probing" switch controls — the cross-question layer of the follow-up
// decision. On by default; off means each question is probed independently.
const SURVEY_LEVEL_PROBING_DETAIL =
  "Follow-ups consider everything the respondent has already said across the whole survey, and skip a probe when they've already answered it under an earlier question.";

// What the single "Adaptive features" switch turns on — shown to the creator so they know exactly
// what they're enabling.
const ADAPTIVE_FEATURE_BULLETS: { title: string; detail: string }[] = [
  {
    title: 'Rubric-driven follow-ups',
    detail:
      'AI follow-ups chase the specific points a great answer should cover, and stop once they’re saturated.',
  },
  {
    title: 'Cross-question coverage',
    detail: 'Won’t re-ask something the respondent already answered under a different question.',
  },
  {
    title: 'Adaptive question scoping',
    detail:
      'Narrows an upcoming open-text question to just its still-uncovered points instead of asking it in full.',
  },
];

/**
 * What the author sees in place of a setting this surface cannot persist.
 *
 * Rendering nothing was the other half of #701: a control that quietly disappears is
 * indistinguishable from a control that was never there, so an author who set it on one screen and
 * cannot find it on another has no way to learn why. The `data-setting-gated` marker is also the
 * hook G1 uses to prove every declared setting is either offered or explained.
 */
const GatedSettingNotice: React.FC<{
  settingKey: SurveyAuthoringSettingKey;
  title: string;
  reason: string;
}> = ({ settingKey, title, reason }) => (
  <div className="mb-6 border border-gray-200 rounded-lg" data-setting-gated={settingKey}>
    <div className="p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-500">{reason}</p>
    </div>
  </div>
);

/**
 * A value fixed when the survey was created: shown, disabled, and EXPLAINED.
 *
 * The alternative that shipped was rendering nothing, which is how the sharpest irreversible
 * decision in the product came to be made in silence — the create form asks for a number of groups
 * that shapes the whole study design, and neither edit surface so much as mentioned it afterwards.
 * A disabled control on its own is barely better: it says "no" without saying why. So the reason
 * rides with it, in the words the create form used before the choice was made.
 */
const LockedSettingControl: React.FC<{
  settingKey: string;
  title: string;
  value: number | string;
  reason: string;
}> = ({ settingKey, title, value, reason }) => (
  <div className="mb-6 border border-gray-200 rounded-lg" data-setting-key={settingKey}>
    <div className="p-4">
      <label
        htmlFor={`locked-${settingKey}`}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        <Lock className="w-3.5 h-3.5 inline mr-1.5 text-gray-400" />
        {title}
      </label>
      <input
        type="text"
        id={`locked-${settingKey}`}
        value={value}
        disabled
        readOnly
        className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-600"
      />
      <p className="text-xs text-gray-500 mt-1">{reason}</p>
    </div>
  </div>
);

const SurveyQuestionsPreview: React.FC<SurveyQuestionsPreviewProps> = ({
  parsedQuestions,
  surveyName,
  focusQuestionId,
  onFocusQuestionHandled,
  issuesByQuestion,
  onQuestionsUpdated,
  onCreateSurvey,
  surface = 'survey_edit',
  onGoBack,
  isLoading = false,
  className = '',
  readOnly = false,
  hideActionButtons = false,
  initialMaxResponses,
  initialAdaptiveProbing,
  initialSurveyLevelProbing,
  initialSurveyStyle,
  initialDiscussionGuide,
  initialMinLinkedInConnections,
  initialTimerConfig,
  initialRedirectConfig,
  initialBlocks,
  groupCount = 1,
  surveyId = null,
  groupSurveyIds = [],
  groupLabels = [],
}) => {
  const [questions, setQuestions] = useState<ParsedQuestion[]>([]);
  /**
   * The selection cap each question's author ASKED for, by question id.
   *
   * Restoring a cap the option count had reduced needs the original number, and nothing else holds
   * it: the question carries the bounded value, which is exactly the information that was lost.
   * The alternatives were both worse — persisting the request would add a second cap field the
   * server has no opinion about, and deferring the bound to save time would leave an
   * activation-blocking value in flight across the many paths that hand `questions` to the parent.
   *
   * STATE, not a ref. It was a ref for two rounds, on the reasoning that it is memory and never a
   * rendered value — and that reasoning is what made round 9's finding 1 possible: the field
   * stayed controlled on the STORED (clamped) number while the request lived somewhere nothing
   * rendered, so a keystroke that moved only the request moved nothing on screen. Now the field
   * SHOWS the request (`editorSelectionCap`), so it is a rendered value and has to be reactive;
   * `react-hooks/refs` says the same thing for the same reason.
   *
   * Every write is a functional update, and the SEED — the one write that reads before it writes —
   * only ever fills a MISSING entry, so successive shrinks keep the original request rather than
   * ratcheting the memory down with the value. Seeding is done by the caller, BEFORE its
   * `setQuestions`, from the question in the render closure: that is what keeps two media
   * uploads in the same tick seeding the same original cap, which reading this state inside a
   * `setQuestions` updater would not (the updater sees the freshly-lowered cap; the closure does
   * not).
   *
   * A stale entry is inert until a cap reappears without the author typing one, and there are two
   * ways that happens, so the map is cleared in two places:
   *
   *   * `questions` being re-synced from `parsedQuestions` — the questions the requests were made
   *     against are being replaced wholesale, so the requests go with them;
   *   * the edit ENDING (`clearPendingEditState`, which Cancel and every Save control runs). This
   *     used to ride on the first clear, because leaving edit mode flipped two DEPS of the re-sync
   *     effect and the effect then re-ran. Phase 4 stopped the effect rebuilding when the incoming
   *     questions say nothing new — which is the point of it — so this forget now has to be its own
   *     statement. It is the case that never fitted the tidy version anyway: Cancel restores a
   *     question that still HAS its cap, so "the cap is null while the map is stale" was false
   *     there, and what made it safe was the effect rather than the value.
   *
   * Two forgets stay redundant and stay absent:
   *   * a type change away from multiple_choice nulls the cap, so every reader that asks
   *     `selectionCap` FIRST answers "nothing to refine" and never reaches the map. That is a
   *     property of the readers, not of the map, and round 10 found the two the author actually
   *     sees did not have it: `editorSelectionCap` and `boundedSelectionCapNote` read the map
   *     before the predicate, so a type change away and BACK left the field showing a 3 that Save
   *     no longer wrote. Both go through `requestedSelectionCap` now, which is where the guard is;
   *   * a delete cannot leave a reachable entry, because a question id is minted from `Date.now()`
   *     or `crypto.randomUUID()` and never reused.
   */
  const [requestedSelectionCaps, setRequestedSelectionCaps] = useState<Record<string, number>>({});
  /**
   * The unusable cap `normalizeSelectionCap` cleared as the survey loaded, by question id.
   *
   * Kept so the repair can be SAID rather than only done. The repair itself is local — the copy
   * the server holds, and validates activation against, keeps its stale 0 until something saves —
   * which is why the same load also marks the editor dirty (see the effect below). Written from
   * the load effect, beside the questions it describes, and rendered, so it is state too.
   */
  const [repairedSelectionCaps, setRepairedSelectionCaps] = useState<Record<string, unknown>>({});
  /**
   * The `parsedQuestions` a repair was already announced for.
   *
   * Marking dirty is a one-shot per incoming question set. Without this, saving a question sets
   * `hasChanges` false, the re-sync effect runs against the SAME (not-yet-round-tripped) prop,
   * finds the same stale 0 and immediately re-raises "Unsaved changes" — the author's save looking
   * like it did nothing.
   */
  const announcedRepairFor = useRef<unknown>(null);
  /**
   * The revision this editor has already taken from its props.
   *
   * Deliberately about the PROP, not about `questions`: it answers "have I applied this exact server
   * content?", never "does the server agree with what I show?" — the author's unsaved work also makes
   * those disagree, and re-syncing over it is the incident useSurveyEditor's docstring records.
   */
  const syncedRevision = useRef<string | null>(null);
  const [blocks, setBlocks] = useState<SurveyBlockDefinition[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [originalQuestionBeforeEdit, setOriginalQuestionBeforeEdit] =
    useState<ParsedQuestion | null>(null);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [editingBlockConditionId, setEditingBlockConditionId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Preview group picker: the QUESTION ID the open picker belongs to, while the user picks a group.
   *
   * The id, not the index it used to hold. An index outlives the question it named — the server's
   * rewrite removing an earlier question leaves the picker open on whatever row slid into that
   * position, and "preview from here" then starts the preview at the wrong question. The id cannot
   * point at the wrong row; at worst it points at nothing, and the re-sync effect clears it.
   */
  const [previewPickerFor, setPreviewPickerFor] = useState<string | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  // Response quota configuration
  const [maxResponses, setMaxResponses] = useState<string>('');
  const [showQuotaConfig, setShowQuotaConfig] = useState(false);

  // AI redundancy skip configuration (ON by default; a survey can opt out)
  const [adaptiveProbing, setAdaptiveProbing] = useState<boolean>(false);

  // Survey-level probing: cross-question follow-up awareness. ON unless the survey opted out, so
  // an unset flag keeps the behaviour every existing survey already has.
  const [surveyLevelProbing, setSurveyLevelProbing] = useState<boolean>(true);

  // Experimental: conversational / discussion-guide survey style (persisted directly via the
  // questions PUT, gated on having a saved surveyId). Off by default → traditional flow untouched.
  const [surveyStyle, setSurveyStyle] = useState<SurveyStyle>('traditional');
  const [discussionGuide, setDiscussionGuide] = useState<DiscussionGuide>({
    guide_markdown: '',
    mandatory_questions: [],
    max_turns: undefined,
  });
  const [convSaveState, setConvSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [convSaveError, setConvSaveError] = useState<string | null>(null);

  // LinkedIn verification configuration
  const [minLinkedInConnections, setMinLinkedInConnections] = useState<string>('');

  // Survey-wide countdown (spec 35). Held as form state (minutes as a string) rather than as a
  // config object, so a half-typed duration cannot become a payload; surveyTimerAuthoring does
  // the conversion, clamping and fail-closed reading in one place.
  const [timerForm, setTimerForm] = useState<TimerFormState>({ ...EMPTY_TIMER_FORM });

  // Panel provider redirect configuration
  const [showRedirectConfig, setShowRedirectConfig] = useState(false);
  // Explicit intent: off => respondents see the internal thank-you page (no external
  // redirect); on => redirect to a panel provider URL. Off is the safe default so a survey
  // never sends respondents to a stale/unconfigured panel link.
  const [redirectEnabled, setRedirectEnabled] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [redirectBaseUrl, setRedirectBaseUrl] = useState('');
  const [screenedOutUrl, setScreenedOutUrl] = useState(''); // Separate URL for screened out participants
  const [trackingParamName, setTrackingParamName] = useState('');
  const [completionParams, setCompletionParams] = useState<string>('');
  const [screenOutParams, setScreenOutParams] = useState<string>('');
  const [quotaFullParams, setQuotaFullParams] = useState<string>('');

  // Media panel expansion state
  const [expandedQuestionMedia, setExpandedQuestionMedia] = useState<string | null>(null);
  const [expandedOptionMedia, setExpandedOptionMedia] = useState<{
    questionId: string;
    optionIndex: number;
  } | null>(null);

  const randomizationPoolsInSurvey = useMemo(() => {
    const s = new Set<string>();
    for (const q of questions) {
      if (q.randomization_group) s.add(q.randomization_group);
    }
    return Array.from(s).sort();
  }, [questions]);

  // Handler for provider selection - auto-fills all fields
  const handleProviderChange = (providerId: string) => {
    setSelectedProvider(providerId);

    if (providerId && PANEL_PROVIDERS[providerId]) {
      const provider = PANEL_PROVIDERS[providerId];
      setRedirectBaseUrl(provider.redirectBaseUrl);
      setScreenedOutUrl(provider.screenedOutUrl);
      setTrackingParamName(provider.trackingParamName);
      setCompletionParams(provider.completionParams);
      setScreenOutParams(provider.screenOutParams);
      setQuotaFullParams(provider.quotaFullParams);
    }
  };

  // Persist the experimental conversational style + discussion guide directly (needs a saved
  // survey). Kept separate from the main questions save so it doesn't touch the callback chain
  // or any parent components.
  const handleSaveConversationalSettings = async () => {
    if (!surveyId) return;
    setConvSaveState('saving');
    setConvSaveError(null);
    try {
      const guidePayload: DiscussionGuide = {
        guide_markdown: discussionGuide.guide_markdown || '',
        mandatory_questions: (discussionGuide.mandatory_questions || []).filter((m) =>
          m.text.trim(),
        ),
        max_turns: discussionGuide.max_turns,
      };
      await updateSurveyQuestions(
        surveyId,
        questions,
        undefined, // redirectConfig
        undefined, // maxResponses
        undefined, // blocks
        undefined, // minLinkedInConnections
        undefined, // enableAiRedundancySkip
        surveyStyle, // surveyStyle
        surveyStyle === 'conversational' ? guidePayload : null, // discussionGuide
      );
      setConvSaveState('saved');
      setTimeout(() => setConvSaveState('idle'), 2500);
    } catch (e) {
      console.error('Failed to save conversational settings:', e);
      setConvSaveError(e instanceof Error ? e.message : 'Failed to save');
      setConvSaveState('error');
    }
  };

  useEffect(() => {
    // Skip state updates if user is actively editing a question OR has unsaved changes
    // This prevents polling (e.g., in SurveyGroupDetails) from blowing away edits in progress
    if (editingQuestionId || hasChanges) {
      return;
    }

    // Map qualitative questions to long_text and set default enable_ai_followup
    const repaired: Record<string, unknown> = {};
    const processedQuestions = parsedQuestions.map((q) => {
      const mapped = {
        ...q,
        type: q.type === 'qualitative' ? 'long_text' : q.type,
        enable_ai_followup: q.enable_ai_followup ?? false, // Disabled by default - users can enable if needed
      };
      const normalized = normalizeSelectionCap(mapped);
      // Identity, not a re-run of the predicate: `normalizeSelectionCap` returns `mapped` itself
      // when it changed nothing.
      if (normalized !== mapped) repaired[mapped.id] = mapped.max_selections;
      return normalized;
    });

    /**
     * Nothing new to take. Rebuilding anyway is what the author feels as the page reloading, and it
     * re-runs every effect below — including the hotspot auto-open, which is why a hotspot question
     * with no image could not be closed. Says nothing about the local copy; the two flags above own that.
     */
    const incomingRevision = questionsRevision(processedQuestions, initialBlocks);
    if (incomingRevision === syncedRevision.current) return;
    syncedRevision.current = incomingRevision;

    // Place that names a question must let go when that question does not come back. The server's
    // own rewrite can drop one (sanitize_redundancy_flags runs on every questions write), and a
    // panel left pointing at a gone id is how the WRONG row ends up expanded.
    const arrivingIds = new Set(processedQuestions.map((q) => q.id));
    setExpandedQuestionMedia((prev) => (prev && !arrivingIds.has(prev) ? null : prev));
    setExpandedOptionMedia((prev) => (prev && !arrivingIds.has(prev.questionId) ? null : prev));
    setPreviewPickerFor((prev) => (prev && !arrivingIds.has(prev) ? null : prev));

    setQuestions(processedQuestions);
    // Same keys => the same object back, so React bails out instead of re-rendering on every poll.
    setRepairedSelectionCaps((prev) => (sameKeys(prev, repaired) ? prev : repaired));
    /**
     * A repair that never becomes dirty may never persist, and that WAS the state: the editor
     * cleared the stale cap in its own copy, showed the author "No limit", and handed the
     * untouched original back to the parent — while the publish validator went on rejecting
     * `max_selections_invalid` against it and the activation button stayed disabled with nothing
     * in the editor able to change that.
     *
     * Marking dirty is not the "silent write" round 3 refused, and the distinction is the whole
     * point: nothing is sent anywhere here. The author still presses Save; this only makes the
     * survey admit it has an unsaved repair, so the save they were already going to make carries
     * it. Gated on `!readOnly` because a read-only view has no Save to carry anything — marking it
     * dirty there would freeze the re-sync effect (which polling depends on) in exchange for
     * nothing at all.
     */
    if (
      Object.keys(repaired).length > 0 &&
      !readOnly &&
      announcedRepairFor.current !== parsedQuestions
    ) {
      announcedRepairFor.current = parsedQuestions;
      setHasChanges(true);
    }
    // These questions are being replaced wholesale, so the caps their author asked for during the
    // last edit stop applying with them. This runs only when nothing is being edited and nothing
    // is unsaved, so it can never discard a request mid-edit — and it is the ONE place a cap can
    // come back without the author typing it, which is the only way a stale request could bite.
    setRequestedSelectionCaps((prev) => (Object.keys(prev).length ? {} : prev));
    // Note: Don't reset hasChanges here - it should only be reset when user explicitly saves

    if (initialBlocks !== undefined) {
      setBlocks(initialBlocks ?? []);
    }

    // Auto-open image_hotspot questions in edit mode if they don't have an image configured
    const firstHotspotWithoutImage = processedQuestions.find(
      (q) => q.type === 'image_hotspot' && !q.image_url,
    );
    if (firstHotspotWithoutImage && !readOnly) {
      setEditingQuestionId(firstHotspotWithoutImage.id);
      setOriginalQuestionBeforeEdit(firstHotspotWithoutImage);
    }
  }, [parsedQuestions, initialBlocks, readOnly, editingQuestionId, hasChanges]);

  // Open a question for edit and scroll it into view — shared by the validation modal's
  // "Go to question" and the per-question issue badge. Never discards other pending edits.
  const focusQuestionForEdit = (questionId: string) => {
    const target = questions.find((q) => q.id === questionId);
    if (!target) return;
    // Capture the Cancel baseline ONCE per open. The effect below re-runs on every `questions`
    // change, so re-capturing here would quietly promote each keystroke to the new "original" and
    // leave Cancel with nothing to revert to — the author's own text, unrecoverable.
    if (editingQuestionId !== target.id) {
      setEditingQuestionId(target.id);
      setOriginalQuestionBeforeEdit(target);
    }
    if (typeof document !== 'undefined') {
      const el = document.getElementById(`question-${target.id}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // "Go to question" from the pre-save validation modal: focus the target, then clear the request.
  useEffect(() => {
    if (!focusQuestionId) return;
    focusQuestionForEdit(focusQuestionId);
    onFocusQuestionHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusQuestionId, questions, onFocusQuestionHandled]);

  // Auto-expand sections in readonly mode
  useEffect(() => {
    if (readOnly) {
      setShowQuotaConfig(true);
      setShowRedirectConfig(true);
    }
  }, [readOnly]);

  // Initialize quota, LinkedIn, and redirect config from props
  useEffect(() => {
    if (initialMaxResponses !== undefined && initialMaxResponses !== null) {
      setMaxResponses(initialMaxResponses.toString());
    }

    if (initialAdaptiveProbing !== undefined) {
      setAdaptiveProbing(initialAdaptiveProbing);
    }

    if (initialSurveyLevelProbing !== undefined) {
      setSurveyLevelProbing(initialSurveyLevelProbing);
    }

    if (initialSurveyStyle !== undefined) {
      setSurveyStyle(initialSurveyStyle);
    }
    if (initialDiscussionGuide) {
      setDiscussionGuide({
        guide_markdown: initialDiscussionGuide.guide_markdown ?? '',
        mandatory_questions: initialDiscussionGuide.mandatory_questions ?? [],
        max_turns: initialDiscussionGuide.max_turns,
      });
    }

    if (initialMinLinkedInConnections !== undefined && initialMinLinkedInConnections !== null) {
      setMinLinkedInConnections(initialMinLinkedInConnections.toString());
    }

    // Unconditional, unlike the guards above: a survey whose timer was REMOVED sends
    // `initialTimerConfig: null`, and skipping that case would leave the form showing the clock
    // it used to be on. timerFormFromConfig fails closed, so null/undefined both reset to OFF.
    setTimerForm(timerFormFromConfig(initialTimerConfig));

    if (initialRedirectConfig) {
      setRedirectEnabled(!!initialRedirectConfig.redirect_base_url);
      setRedirectBaseUrl(initialRedirectConfig.redirect_base_url || '');
      setScreenedOutUrl(initialRedirectConfig.screened_out_url || '');

      // Extract tracking param name (first key in tracking_param object)
      const trackingKeys = Object.keys(initialRedirectConfig.tracking_param || {});
      if (trackingKeys.length > 0) {
        setTrackingParamName(trackingKeys[0]);
      }

      // Convert param objects to comma-separated strings
      const formatParams = (params: { [key: string]: string } | null) => {
        if (!params) return '';
        return Object.entries(params)
          .map(([key, value]) => `${key}=${value}`)
          .join(', ');
      };

      setCompletionParams(formatParams(initialRedirectConfig.completion_params));
      setScreenOutParams(formatParams(initialRedirectConfig.screen_out_params));
      setQuotaFullParams(formatParams(initialRedirectConfig.quota_full_params));
    }
  }, [
    initialMaxResponses,
    initialAdaptiveProbing,
    initialSurveyLevelProbing,
    initialMinLinkedInConnections,
    initialTimerConfig,
    initialRedirectConfig,
  ]);

  const openPreview = (groupSurveyId: number, startFrom?: number) => {
    const shareId = surveyIdToShareId(groupSurveyId);
    const params =
      startFrom !== undefined ? `?preview=true&startFrom=${startFrom}` : '?preview=true';
    window.open(`/surveys/${shareId}/take${params}`, '_blank');
    setPreviewPickerFor(null);
  };

  const renderVideoThumb = (url: string, fullUrl?: string) => {
    const thumb = getYouTubeThumbnail(url);
    const previewSrc = isExternalStreamingVideoUrl(url) ? getEmbedUrl(url) || url : fullUrl || url;
    return (
      <button
        type="button"
        onClick={() => setVideoPreviewUrl(previewSrc)}
        className="w-full h-full relative bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center"
      >
        {thumb && (
          <img
            src={thumb}
            alt="Video"
            className="absolute inset-0 w-full h-full object-cover opacity-80"
          />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <svg
            className="w-4 h-4 text-white/70"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
            <line x1="7" y1="2" x2="7" y2="22" />
            <line x1="17" y1="2" x2="17" y2="22" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <line x1="2" y1="7" x2="7" y2="7" />
            <line x1="2" y1="17" x2="7" y2="17" />
            <line x1="17" y1="7" x2="22" y2="7" />
            <line x1="17" y1="17" x2="22" y2="17" />
          </svg>
          <span className="w-4 h-4 bg-white/90 rounded-full flex items-center justify-center">
            <span className="ml-0.5 border-l-[5px] border-l-gray-800 border-y-[3px] border-y-transparent" />
          </span>
        </div>
      </button>
    );
  };

  /**
   * `questionId` is what the picker remembers; `questionIndex` is only the deep-link's start
   * position, and it is resolved again from the CURRENT list when a group is finally chosen.
   */
  const handlePreviewClick = (questionId: string | null, questionIndex?: number) => {
    const isGroup = groupSurveyIds.length > 1;
    const targetId = isGroup ? null : surveyId;

    if (!isGroup && targetId) {
      openPreview(targetId, questionIndex);
    } else if (isGroup) {
      setPreviewPickerFor(questionId);
    }
  };

  const updateQuestion = (questionId: string, updates: Partial<ParsedQuestion>) => {
    const updatedQuestions = questions.map((q) => (q.id === questionId ? { ...q, ...updates } : q));
    setQuestions(updatedQuestions);
    setHasChanges(true);
    // Don't save to parent until user clicks save
  };

  /**
   * The number a question's author asked for: what is remembered, or — the SEED — the cap already
   * on the question when nothing is remembered yet.
   *
   * The seed is what restores a cap the AI parser set, or one from a previous session, after a
   * removal, exactly as it restores a freshly typed one. Pure: it reads, and the caller decides
   * whether to remember. Undefined for a question carrying no cap, which is the "nothing to
   * restore" case.
   */
  const requestedSelectionCap = (question: ParsedQuestion): number | undefined => {
    const cap = selectionCap(question);
    if (cap === null) return undefined;
    return requestedSelectionCaps[question.id] ?? cap;
  };

  /**
   * Remember a request, filling a MISSING entry only.
   *
   * That is the whole ratchet fix in one line: successive shrinks keep the original request rather
   * than lowering the memory alongside the value.
   */
  const rememberSelectionCap = (questionId: string, requested: number | undefined) => {
    if (requested === undefined) return;
    setRequestedSelectionCaps((prev) =>
      prev[questionId] === undefined ? { ...prev, [questionId]: requested } : prev,
    );
  };

  /**
   * What the cap FIELD shows: the number the author asked for, not the number that survived the
   * bound.
   *
   * It used to show the stored value while the request lived only in a ref nothing rendered, and
   * that combination is what made round 6's restoration dangerous. Four choices, an author typing
   * `40` by mistake: after the `4` the field read 4, after the `0` it STILL read 4, so nothing on
   * screen said the keystroke had landed. The author believed the cap was 4, added a fifth choice,
   * and the cap silently became 5 — climbing towards a 40 they had no way to see or clear short of
   * re-typing the field.
   *
   * Showing the request fixes both halves at once: every keystroke moves something visible, and
   * emptying the field is once again the affordance that clears the memory. What is STORED is
   * unchanged — still `min(request, options)` — so the payload, the summary and the publish
   * validator see exactly what they saw before. `MAX_SELECTIONS_BOUNDED_NOTE` says which number is
   * in force whenever the two differ, so the field is never read as a promise.
   *
   * Through `requestedSelectionCap`, not the map directly: a request is only ever a REFINEMENT of a
   * cap the question carries, and reading the map first said otherwise. A type change away from
   * multiple_choice and back nulls the cap while the memory survives (it is cleared on re-sync, not
   * per action), so the field went on showing the 3 the author typed while Save wrote null —
   * exactly the silent disagreement between field and stored value that showing the request exists
   * to end.
   */
  const editorSelectionCap = (question: ParsedQuestion): number | '' =>
    requestedSelectionCap(question) ?? '';

  /**
   * The "your number is bigger than this question" line, or null when nothing is being bounded.
   *
   * Gated on the question CARRYING a cap for the same reason `editorSelectionCap` is: a request the
   * field no longer shows must not be explained either, or the note becomes a second claim the save
   * contradicts. The map is still read directly afterwards, because this line is specifically about
   * a number the author ASKED for being bounded — a stored cap that already exceeds its options is
   * a different defect, and `reclampedSelectionCap` repairs it on the next edit rather than
   * announcing a limit that is not the one in force.
   */
  const boundedSelectionCapNote = (question: ParsedQuestion): string | null => {
    const requested =
      selectionCap(question) === null ? undefined : requestedSelectionCaps[question.id];
    const limit = maxSelectableOptions(question);
    if (requested === undefined || limit === undefined || requested <= limit) return null;
    return MAX_SELECTIONS_BOUNDED_NOTE(limit);
  };

  /** The "this survey arrived with an unusable cap" line, or null when it did not. */
  const repairedSelectionCapNote = (question: ParsedQuestion): string | null => {
    const stored = repairedSelectionCaps[question.id];
    return stored === undefined ? null : MAX_SELECTIONS_REPAIRED_NOTE(stored);
  };

  /**
   * Patch a question whose patch CHANGES how many options it offers, re-deriving the selection cap
   * from what its author asked for and what is now there. Every path that adds or removes a
   * selectable option goes through here, so a cap can never outlive the options it was set
   * against — nor stay stuck below a request the options have since grown to meet.
   */
  const updateQuestionOptions = (questionId: string, updates: Partial<ParsedQuestion>) => {
    const question = questions.find((q) => q.id === questionId);
    const patched = question ? { ...question, ...updates } : undefined;
    const requested = patched ? requestedSelectionCap(patched) : undefined;
    rememberSelectionCap(questionId, requested);
    const cap = patched ? reclampedSelectionCap(patched, requested) : undefined;
    updateQuestion(questionId, cap === undefined ? updates : { ...updates, max_selections: cap });
  };

  /**
   * The same re-clamp, applied to an already-patched question rather than to a patch.
   *
   * The media handlers below build their next question inside a functional `setQuestions` (they
   * have to: they are called in rapid succession by the upload grid and would otherwise read stale
   * state), so they cannot go through `updateQuestionOptions`, which resolves the question from the
   * render's `questions`. They changed the selectable option count and never re-clamped.
   *
   * Both directions matter on a legacy choice-level-media question, where the count is the TILES:
   * removing a tile shrinks it, and ADDING the first tile shrinks it too — the question stops
   * offering its choice list and starts offering one image. Per-option media does the same in
   * reverse, which is why `addOptionMedia`/`removeOptionMedia` are here as well.
   *
   * The request is an ARGUMENT, not a read of `requestedSelectionCaps`. Reading the map here read
   * it from the render closure, where the seed `rememberSelectionCapFor` had just written could not
   * yet appear — it is a state write, and the whole batch shares one render. `MediaUploadGrid`
   * awaits `Promise.all` over the uploads and then calls `onMediaAdd` once per result in a
   * synchronous `forEach`, so a 3-file drop on a legacy choice-media question clamped to the FIRST
   * tile (`min(cap, 1)`) and the remaining two calls, seeing no request, held it there. What every
   * call in a synchronous batch can agree on is a value computed from the closure they share and
   * passed by value; a state write is only ever visible to the NEXT render.
   */
  const withReclampedCap = (
    question: ParsedQuestion,
    requested: number | undefined,
  ): ParsedQuestion => {
    const cap = reclampedSelectionCap(question, requested);
    return cap === undefined ? question : { ...question, max_selections: cap };
  };

  /**
   * Seed the memory for a question whose media handler is about to change its option count, and
   * hand the seeded number back for that handler to carry through its updater.
   *
   * Runs BEFORE the handler's `setQuestions`, and reads the question out of the RENDER CLOSURE
   * rather than out of the updater. That distinction is the whole reason it exists: the upload
   * grid calls those handlers in rapid succession within one tick, and the updater's copy already
   * carries the previous call's lowered cap while the closure still carries the original. Seeding
   * from the updater would ratchet the memory down exactly as the stored value used to.
   *
   * RETURNING the value is the other half, and the half round 9 left out. The map write lands on
   * the next render — soon enough for the field and for a LATER tick's restore, never soon enough
   * for the next call in the same batch. Only the returned value crosses that gap.
   */
  const rememberSelectionCapFor = (questionId: string): number | undefined => {
    const question = questions.find((q) => q.id === questionId);
    if (!question) return undefined;
    const requested = requestedSelectionCap(question);
    rememberSelectionCap(questionId, requested);
    return requested;
  };

  /**
   * Change a question's type, dropping the settings the new type cannot express.
   *
   * `max_selections` is the one that bites: its editor renders for multiple_choice ONLY, so once
   * the type moved the author had no control that could remove a value they could no longer see —
   * while the publish validator flagged it and blocked activation. Clearing it here is what stops
   * that state from ever being created. (The backend `clear_max_selections` auto-fix repairs the
   * surveys already carrying one.)
   */
  const changeQuestionType = (questionId: string, nextType: string) => {
    const updates: Partial<ParsedQuestion> = { type: nextType };
    if (nextType !== MULTI_SELECT_TYPE) updates.max_selections = null;
    updateQuestion(questionId, updates);
  };

  const removeQuestion = (questionId: string) => {
    const conditionReferencesQuestion = (dc: any, qid: string): boolean => {
      if (!dc) return false;
      if (dc.source_question_id === qid) return true;
      if (Array.isArray(dc.conditions)) {
        return dc.conditions.some((c: any) => c.source_question_id === qid);
      }
      return false;
    };

    const dependentQuestions = questions.filter((q) =>
      conditionReferencesQuestion(q.display_condition, questionId),
    );

    let confirmMessage =
      'Are you sure you want to delete this question? This action cannot be undone.';
    if (dependentQuestions.length > 0) {
      const questionIndex = questions.findIndex((q) => q.id === questionId) + 1;
      const dependentList = dependentQuestions
        .map((q) => `Q${questions.findIndex((dq) => dq.id === q.id) + 1}`)
        .join(', ');
      confirmMessage = `Question Q${questionIndex} is used as a condition source for: ${dependentList}.\n\nDeleting this question will remove the display conditions from these dependent questions.\n\nAre you sure you want to continue?`;
    }

    if (window.confirm(confirmMessage)) {
      const updatedQuestions = questions
        .filter((q) => q.id !== questionId)
        .map((q) => {
          if (!conditionReferencesQuestion(q.display_condition, questionId)) return q;
          const dc = q.display_condition;
          if (dc?.source_question_id === questionId) {
            const { display_condition, ...rest } = q;
            return rest as ParsedQuestion;
          }
          if (Array.isArray(dc?.conditions)) {
            const remaining = dc.conditions.filter((c: any) => c.source_question_id !== questionId);
            if (remaining.length === 0) {
              const { display_condition, ...rest } = q;
              return rest as ParsedQuestion;
            }
            return { ...q, display_condition: { ...dc, conditions: remaining } };
          }
          return q;
        });

      setQuestions(updatedQuestions);
      setHasChanges(true);
      emitStructuralChange(updatedQuestions, blocks);

      // If we were editing this question, clear edit state
      if (editingQuestionId === questionId) {
        setEditingQuestionId(null);
        setOriginalQuestionBeforeEdit(null);
      }
    }
  };

  const addNewQuestion = () => {
    insertQuestionAt(questions.length);
  };

  // Insert a new question at a specific index
  const insertQuestionAt = (index: number) => {
    const newQuestion: ParsedQuestion = {
      id: `question_${Date.now()}`,
      text: '',
      type: 'single_choice',
      choices: ['Option 1', 'Option 2'],
      required: true,
      enable_ai_followup: false,
    };

    const updatedQuestions = [...questions.slice(0, index), newQuestion, ...questions.slice(index)];
    setQuestions(updatedQuestions);
    setHasChanges(true);
    emitStructuralChange(updatedQuestions, blocks);

    // Automatically open the new question for editing
    setOriginalQuestionBeforeEdit(newQuestion);
    setEditingQuestionId(newQuestion.id);
  };

  // Duplicate an existing question with all its settings
  const duplicateQuestion = (questionId: string) => {
    try {
      const sourceQuestion = questions.find((q) => q.id === questionId);
      if (!sourceQuestion) {
        console.error('Cannot duplicate: question not found', questionId);
        return;
      }

      // Deep clone using structuredClone (modern, type-safe, handles edge cases)
      const duplicatedQuestion: ParsedQuestion = {
        ...structuredClone(sourceQuestion),
        id: `question_${crypto.randomUUID()}`,
      };

      // For image_hotspot questions, regenerate bounding box IDs to avoid selection state conflicts
      if (duplicatedQuestion.type === 'image_hotspot' && duplicatedQuestion.bounding_boxes) {
        duplicatedQuestion.bounding_boxes = duplicatedQuestion.bounding_boxes.map((box) => ({
          ...box,
          id: `box_${crypto.randomUUID()}`,
        }));
      }

      // Find index and insert after the original
      const sourceIndex = questions.findIndex((q) => q.id === questionId);
      const updatedQuestions = [
        ...questions.slice(0, sourceIndex + 1),
        duplicatedQuestion,
        ...questions.slice(sourceIndex + 1),
      ];

      setQuestions(updatedQuestions);
      setHasChanges(true);
      emitStructuralChange(updatedQuestions, blocks);

      // Open the duplicate in edit mode for immediate customization
      setOriginalQuestionBeforeEdit(duplicatedQuestion);
      setEditingQuestionId(duplicatedQuestion.id);
    } catch (error) {
      console.error('Failed to duplicate question:', error);
    }
  };

  const getBlockLabel = (blockId: string) => blocks.find((b) => b.id === blockId)?.label ?? blockId;

  const duplicateBlock = (blockId: string) => {
    const meta = blocks.find((b) => b.id === blockId);
    const indices = questions
      .map((q, i) => (q.block_id === blockId ? i : -1))
      .filter((i) => i >= 0);
    if (indices.length === 0) return;

    const newBlockId = `block_${crypto.randomUUID()}`;
    const newMeta: SurveyBlockDefinition = {
      id: newBlockId,
      label: `${meta?.label ?? 'Block'} copy`,
      randomize_within: meta?.randomize_within ?? false,
    };
    const lastIdx = Math.max(...indices);

    const dupQs: ParsedQuestion[] = indices.map((i) => {
      const sq = questions[i];
      const dq: ParsedQuestion = {
        ...structuredClone(sq),
        id: `question_${crypto.randomUUID()}`,
        block_id: newBlockId,
      };
      if (dq.type === 'image_hotspot' && dq.bounding_boxes) {
        dq.bounding_boxes = dq.bounding_boxes.map((box) => ({
          ...box,
          id: `box_${crypto.randomUUID()}`,
        }));
      }
      return dq;
    });

    const nextBlocks = [...blocks, newMeta];
    const newQuestions = [
      ...questions.slice(0, lastIdx + 1),
      ...dupQs,
      ...questions.slice(lastIdx + 1),
    ];

    setBlocks(nextBlocks);
    setQuestions(newQuestions);
    setHasChanges(true);
    emitStructuralChange(newQuestions, nextBlocks);
  };

  const updateBlockRandomizeWithin = (blockId: string, value: boolean) => {
    const next = blocks.map((b) => (b.id === blockId ? { ...b, randomize_within: value } : b));
    setBlocks(next);
    setHasChanges(true);
    emitStructuralChange(questions, next);
  };

  const updateBlockDisplayCondition = (
    blockId: string,
    displayCondition: SurveyBlockDefinition['display_condition'] | undefined,
  ) => {
    const next = blocks.map((b) =>
      b.id === blockId ? { ...b, display_condition: displayCondition } : b,
    );
    setBlocks(next);
    setHasChanges(true);
    emitStructuralChange(questions, next);
  };

  const updateBlockLabel = (blockId: string, newLabel: string) => {
    const next = blocks.map((b) => (b.id === blockId ? { ...b, label: newLabel } : b));
    setBlocks(next);
    setHasChanges(true);
    emitStructuralChange(questions, next);
  };

  /** Turn rapid-fire on/off for a block, or patch one of its settings. */
  const updateBlockRapidFire = (blockId: string, patch: Partial<RapidFireConfig> | null) => {
    const next = blocks.map((b) => {
      if (b.id !== blockId) return b;
      if (patch === null) return { ...b, rapid_fire: null };
      const current = b.rapid_fire ?? DEFAULT_RAPID_FIRE_CONFIG;
      return { ...b, rapid_fire: { ...current, ...patch } };
    });
    setBlocks(next);
    setHasChanges(true);
    emitStructuralChange(questions, next);
  };

  const addChoice = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      const choices = question.choices || [];
      updateQuestionOptions(questionId, {
        choices: [...choices, `Option ${choices.length + 1}`],
      });
    }
  };

  const removeChoice = (questionId: string, choiceIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question && question.choices) {
      const choices = question.choices.filter((_, index) => index !== choiceIndex);
      // Also remove the corresponding media URL, but only where `media_urls` is actually
      // choice-indexed: a type that renders it that way AND the legacy explicit 'choices' mode.
      // Both conditions are required, and neither implies the other — the mode survives a retype
      // that changes what the media means, and the type alone says nothing about a modern
      // question whose media_urls is the stimulus. Anywhere else `media_urls` is the QUESTION's
      // own stimulus, and filtering it by the deleted choice's index silently deletes the
      // researcher's Nth concept image.
      const isChoiceIndexedMedia =
        CHOICE_INDEXED_MEDIA_TYPES.includes(question.type) &&
        question.media_display_mode === 'choices';
      const mediaUrls =
        question.media_urls && isChoiceIndexedMedia
          ? question.media_urls.filter((_, index) => index !== choiceIndex)
          : question.media_urls;

      // Remove the corresponding choice_action at the same index
      const choiceActions = question.choice_actions
        ? question.choice_actions.filter((_, index) => index !== choiceIndex)
        : undefined;

      // Re-index option_media keys (shift indices down for entries after removed index)
      let optionMedia: { [optionIndex: number]: string[] } | undefined = undefined;
      if (question.option_media) {
        optionMedia = {};
        Object.entries(question.option_media).forEach(([key, urls]) => {
          const oldIndex = parseInt(key);
          if (oldIndex < choiceIndex) {
            // Keep indices before removed choice unchanged
            optionMedia![oldIndex] = urls;
          } else if (oldIndex > choiceIndex) {
            // Shift indices after removed choice down by 1
            optionMedia![oldIndex - 1] = urls;
          }
          // Skip the removed choice index entirely
        });
        // Clean up if empty
        if (Object.keys(optionMedia).length === 0) {
          optionMedia = undefined;
        }
      }

      // The per-group sidecar is keyed by option index too, so it has to shift with it — otherwise
      // deleting a claim hands every later one the PREVIOUS claim's per-group artwork, which is
      // the same defect the option_media re-index above exists to prevent.
      let optionGroupMedia: Record<string, Record<string, string[]>> | undefined = undefined;
      if (question.option_group_media) {
        optionGroupMedia = {};
        Object.entries(question.option_group_media).forEach(([key, perGroup]) => {
          const oldIndex = parseInt(key);
          if (oldIndex < choiceIndex) optionGroupMedia![String(oldIndex)] = perGroup;
          else if (oldIndex > choiceIndex) optionGroupMedia![String(oldIndex - 1)] = perGroup;
        });
        if (Object.keys(optionGroupMedia).length === 0) {
          optionGroupMedia = undefined;
        }
      }

      // `updateQuestionOptions`, NOT `updateQuestion`: deleting a choice removes a selectable
      // option, and that path has to re-derive the multi-select cap (spec 36) or a cap can outlive
      // the options it was set against.
      updateQuestionOptions(questionId, {
        choices,
        media_urls: mediaUrls && mediaUrls.length > 0 ? mediaUrls : undefined,
        choice_actions: choiceActions && choiceActions.length > 0 ? choiceActions : undefined,
        option_media: optionMedia,
        option_group_media: optionGroupMedia,
      });
    }
  };

  const updateChoice = (questionId: string, choiceIndex: number, value: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question && question.choices) {
      const choices = [...question.choices];
      choices[choiceIndex] = value;

      // Also update the value field in choice_actions to keep them in sync
      // (backend matches skip logic by value field)
      let choiceActions = question.choice_actions;
      if (choiceActions && choiceActions[choiceIndex]) {
        choiceActions = [...choiceActions];
        choiceActions[choiceIndex] = {
          ...choiceActions[choiceIndex],
          value: value,
        };
      }

      updateQuestion(questionId, {
        choices,
        ...(choiceActions ? { choice_actions: choiceActions } : {}),
      });
    }
  };

  // Helper functions for question-level media management
  // Using functional state updates to handle rapid successive calls properly
  const addQuestionMedia = (questionId: string, url: string) => {
    const requested = rememberSelectionCapFor(questionId);
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId) return q;
        return withReclampedCap({ ...q, media_urls: [...(q.media_urls || []), url] }, requested);
      }),
    );
    setHasChanges(true);
  };

  const removeQuestionMedia = (questionId: string, index: number) => {
    const requested = rememberSelectionCapFor(questionId);
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId || !q.media_urls) return q;
        const next = q.media_urls.filter((_, i) => i !== index);
        const updates: Partial<ParsedQuestion> = {
          media_urls: next.length > 0 ? next : undefined,
        };
        if (next.length < 2) {
          updates.randomize_media_order = false;
        }
        return withReclampedCap({ ...q, ...updates }, requested);
      }),
    );
    setHasChanges(true);
  };

  const replaceQuestionMedia = (questionId: string, index: number, url: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === questionId && q.media_urls
          ? { ...q, media_urls: q.media_urls.map((u, i) => (i === index ? url : u)) }
          : q,
      ),
    );
    setHasChanges(true);
  };

  const reorderQuestionMedia = (questionId: string, fromIndex: number, toIndex: number) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId || !q.media_urls) return q;
        const urls = [...q.media_urls];
        const [moved] = urls.splice(fromIndex, 1);
        urls.splice(toIndex, 0, moved);
        return { ...q, media_urls: urls };
      }),
    );
    setHasChanges(true);
  };

  /**
   * Helper functions for option-level media management.
   *
   * Adding the FIRST per-option media and removing the LAST both re-clamp, because `option_media`
   * is the third input to `rendersImageTiles` — so either one flips the selectable option count
   * between the tiles and the choice list without touching `media_urls` at all. Six tiles with two
   * labels and a cap of 4 is valid; attach per-option media and the same question offers two
   * options with a stored cap of 4, which the editor still shows as valid and the publish
   * validator rejects as `max_selections_exceeds_choices`.
   *
   * `replaceOptionMedia` and `reorderOptionMedia` write the same field and deliberately do not:
   * each requires the entry it edits to exist and writes it back under the same key, so neither
   * the key count nor the url count can move, and a re-clamp there would be a no-op dressed as a
   * rule. The tests pin that rather than leaving it as a reading.
   */
  const addOptionMedia = (questionId: string, optionIndex: number, url: string) => {
    const requested = rememberSelectionCapFor(questionId);
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId) return q;
        const optionMedia = { ...(q.option_media || {}) };
        optionMedia[optionIndex] = [...(optionMedia[optionIndex] || []), url];
        return withReclampedCap({ ...q, option_media: optionMedia }, requested);
      }),
    );
    setHasChanges(true);
  };

  const removeOptionMedia = (questionId: string, optionIndex: number, mediaIndex: number) => {
    const requested = rememberSelectionCapFor(questionId);
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId || !q.option_media || !q.option_media[optionIndex]) return q;
        const optionMedia = { ...(q.option_media || {}) };
        optionMedia[optionIndex] = optionMedia[optionIndex].filter((_, i) => i !== mediaIndex);
        // Clean up empty arrays
        if (optionMedia[optionIndex].length === 0) {
          delete optionMedia[optionIndex];
        }
        return withReclampedCap({ ...q, option_media: optionMedia }, requested);
      }),
    );
    setHasChanges(true);
  };

  const replaceOptionMedia = (
    questionId: string,
    optionIndex: number,
    mediaIndex: number,
    url: string,
  ) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId || !q.option_media) return q;
        const om = q.option_media;
        const existing =
          om[optionIndex] ?? (om as Record<string, string[] | undefined>)[String(optionIndex)];
        if (!existing || mediaIndex < 0 || mediaIndex >= existing.length) return q;
        const optionMedia = { ...om };
        optionMedia[optionIndex] = existing.map((u, i) => (i === mediaIndex ? url : u));
        return { ...q, option_media: optionMedia };
      }),
    );
    setHasChanges(true);
  };

  const reorderOptionMedia = (
    questionId: string,
    optionIndex: number,
    fromIndex: number,
    toIndex: number,
  ) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId || !q.option_media) return q;
        const om = q.option_media;
        const existing =
          om[optionIndex] ?? (om as Record<string, string[] | undefined>)[String(optionIndex)];
        if (!existing) return q;
        const urls = [...existing];
        const [moved] = urls.splice(fromIndex, 1);
        urls.splice(toIndex, 0, moved);
        const optionMedia = { ...om };
        optionMedia[optionIndex] = urls;
        return { ...q, option_media: optionMedia };
      }),
    );
    setHasChanges(true);
  };

  const getOptionMediaUrls = (question: ParsedQuestion, optionIndex: number): string[] => {
    const om = question.option_media;
    if (!om) return [];
    const byNum = om[optionIndex];
    if (byNum) return byNum;
    const asRecord = om as Record<string, string[] | undefined>;
    return asRecord[String(optionIndex)] ?? [];
  };

  // --- per-group option media (`option_group_media[optionIndex][groupIndex]`) ------------------
  // A grouped survey serves each respondent ONE group's version of a question, and
  // `_build_questions_for_group` resolves this sidecar down onto `option_media` for the group being
  // served. Every surface that offers option media offers this too — choice rows and, since a
  // packaging A/B is the reason to put pictures on claims at all, MaxDiff claim rows.

  const getOptionGroupMediaUrls = (
    question: ParsedQuestion,
    optionIndex: number,
  ): Record<string, string[]> => question.option_group_media?.[String(optionIndex)] ?? {};

  const countGroupMedia = (groupMedia: Record<string, string[]>): number =>
    Object.values(groupMedia).reduce((total, urls) => total + (urls?.length ?? 0), 0);

  /** Apply `mutate` to one option's per-group map, dropping the map (and the field) once empty. */
  const updateOptionGroupMedia = (
    questionId: string,
    optionIndex: number,
    mutate: (perOption: Record<string, string[]>) => Record<string, string[]> | null,
  ) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id !== questionId) return q;
        const byOption = { ...(q.option_group_media || {}) };
        const next = mutate({ ...(byOption[String(optionIndex)] || {}) });
        if (next === null) return q;
        if (Object.keys(next).length > 0) byOption[String(optionIndex)] = next;
        else delete byOption[String(optionIndex)];
        return {
          ...q,
          option_group_media: Object.keys(byOption).length > 0 ? byOption : undefined,
        };
      }),
    );
    setHasChanges(true);
  };

  const addOptionGroupMedia = (
    questionId: string,
    optionIndex: number,
    groupIndex: number,
    url: string,
  ) =>
    updateOptionGroupMedia(questionId, optionIndex, (perOption) => ({
      ...perOption,
      [String(groupIndex)]: [...(perOption[String(groupIndex)] || []), url],
    }));

  const removeOptionGroupMedia = (
    questionId: string,
    optionIndex: number,
    groupIndex: number,
    mediaIndex: number,
  ) =>
    updateOptionGroupMedia(questionId, optionIndex, (perOption) => {
      const urls = (perOption[String(groupIndex)] || []).filter((_, i) => i !== mediaIndex);
      const next = { ...perOption };
      if (urls.length > 0) next[String(groupIndex)] = urls;
      else delete next[String(groupIndex)];
      return next;
    });

  const replaceOptionGroupMedia = (
    questionId: string,
    optionIndex: number,
    groupIndex: number,
    mediaIndex: number,
    url: string,
  ) =>
    updateOptionGroupMedia(questionId, optionIndex, (perOption) => {
      const urls = [...(perOption[String(groupIndex)] || [])];
      // Out of range: leave the question untouched rather than growing the array with a hole.
      if (mediaIndex < 0 || mediaIndex >= urls.length) return null;
      urls[mediaIndex] = url;
      return { ...perOption, [String(groupIndex)]: urls };
    });

  // Helper functions for managing choice actions (screener/skip logic)
  const updateChoiceAction = (
    questionId: string,
    choiceIndex: number,
    action?: 'terminate' | 'skip_to',
    target?: string,
    reason?: string,
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question || !question.choices) return;

    // Get or create choice_actions array matching choices length
    const choiceActions: ChoiceAction[] = question.choices.map((choice, idx) => {
      const existingAction = question.choice_actions?.[idx];

      if (idx === choiceIndex) {
        // Update this choice's action
        return {
          value: choice,
          action,
          target: action === 'skip_to' ? target : undefined,
          reason: action === 'terminate' ? reason : undefined,
        };
      }

      // Preserve existing action or create default
      return existingAction || { value: choice };
    });

    updateQuestion(questionId, { choice_actions: choiceActions });
  };

  // Get action metadata for a specific choice
  const getChoiceAction = (
    question: ParsedQuestion,
    choiceIndex: number,
  ): ChoiceAction | undefined => {
    return question.choice_actions?.[choiceIndex];
  };

  const addMatrixItem = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      const items = question.matrix_rows || [];
      updateQuestion(questionId, {
        matrix_rows: [...items, `Item ${items.length + 1}`],
      });
    }
  };

  const removeMatrixItem = (questionId: string, itemIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question && question.matrix_rows) {
      const items = question.matrix_rows.filter((_, index) => index !== itemIndex);
      updateQuestion(questionId, { matrix_rows: items });
    }
  };

  const updateMatrixItem = (questionId: string, itemIndex: number, value: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question && question.matrix_rows) {
      const items = [...question.matrix_rows];
      items[itemIndex] = value;
      updateQuestion(questionId, { matrix_rows: items });
    }
  };

  const addConjointAttribute = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      const attributes = question.conjoint_attributes || [];
      updateQuestion(questionId, {
        conjoint_attributes: [
          ...attributes,
          { name: `Attribute ${attributes.length + 1}`, levels: ['Level 1', 'Level 2'] },
        ],
      });
    }
  };

  const removeConjointAttribute = (questionId: string, attrIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = question.conjoint_attributes.filter((_, index) => index !== attrIndex);
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointAttribute = (
    questionId: string,
    attrIndex: number,
    field: string,
    value: any,
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      attributes[attrIndex] = { ...attributes[attrIndex], [field]: value };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const questionType = QUESTION_TYPES.find((qt) => qt.value === type);
    return questionType ? questionType.label : type;
  };

  const hasChoices = (type: string) => {
    return CHOICE_BASED_TYPES.includes(type);
  };

  const hasScale = (type: string) => {
    return SCALE_BASED_TYPES.includes(type);
  };

  const hasRanking = (type: string) => {
    return RANKING_BASED_TYPES.includes(type);
  };

  const hasMatrix = (type: string) => {
    return MATRIX_BASED_TYPES.includes(type);
  };

  const isMultiSelect = (type: string) => type === MULTI_SELECT_TYPE;

  const hasMaxDiff = (type: string) => {
    return MAX_DIFF_BASED_TYPES.includes(type);
  };

  const hasConjoint = (type: string) => {
    return CONJOINT_BASED_TYPES.includes(type);
  };

  const hasImageHotspot = (type: string) => {
    return IMAGE_HOTSPOT_TYPES.includes(type);
  };

  const hasAllocation = (type: string) => {
    return ALLOCATION_BASED_TYPES.includes(type);
  };

  const hasPhotoUpload = (type: string) => {
    return PHOTO_UPLOAD_TYPES.includes(type);
  };

  // Helper function to parse comma-separated key=value pairs into object
  const parseParams = (paramsString: string): { [key: string]: string } => {
    if (!paramsString.trim()) return {};

    const params: { [key: string]: string } = {};
    const pairs = paramsString
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p);

    for (const pair of pairs) {
      const [key, value] = pair.split('=').map((s) => s.trim());
      if (key && value) {
        params[key] = value;
      }
    }

    return params;
  };

  // Single source of truth for what redirect_config every save path sends. Returns null
  // when redirect is off so the backend clears any previously-saved panel config (an
  // omitted value would instead leave a stale redirect in place).
  const buildRedirectConfigForSave = (): RedirectConfig | null => {
    if (!redirectEnabled) return null;

    const completionParamsObj = parseParams(completionParams);
    const screenOutParamsObj = parseParams(screenOutParams);
    const quotaFullParamsObj = parseParams(quotaFullParams);

    const trackingParam: { [key: string]: string } = {};
    if (trackingParamName.trim()) {
      trackingParam[trackingParamName.trim()] = '{TRACKING_ID}';
    }

    return {
      redirect_base_url: redirectBaseUrl.trim(),
      screened_out_url: screenedOutUrl.trim() || null,
      tracking_param: trackingParam,
      completion_params: Object.keys(completionParamsObj).length > 0 ? completionParamsObj : null,
      screen_out_params: Object.keys(screenOutParamsObj).length > 0 ? screenOutParamsObj : null,
      quota_full_params: Object.keys(quotaFullParamsObj).length > 0 ? quotaFullParamsObj : null,
    };
  };

  // When redirect is enabled the base URL is required; block saves until it is provided so
  // we never persist an enabled-but-empty (broken) redirect.
  const redirectConfigInvalid = redirectEnabled && !redirectBaseUrl.trim();

  /**
   * The surface this editor is REALLY on, which the parent declares — a component cannot infer it
   * from its props without guessing, and guessing wrong is how a setting gets dropped.
   */
  const effectiveSurface: SurveyAuthoringSurface = surveyId ? surface : 'create';

  /** Why this surface cannot persist a setting, or null when it can. */
  const gatedReason = (key: SurveyAuthoringSettingKey) => unsupportedReason(key, effectiveSurface);

  /**
   * The whole settings payload, assembled in exactly one place.
   *
   * A setting this surface cannot persist is sent as `undefined` ("leave it alone") rather than as
   * the form's value: the control is not offered there, so the form holds a default nobody chose,
   * and sending it would CLEAR the stored value instead of preserving it. That is why the outbound
   * fix cannot outrun the inbound prefill — see `surveyAuthoringSettings`.
   */
  const settingsFromForm = (): SurveyAuthoringSettings => {
    const valueIfSupported = <T,>(key: SurveyAuthoringSettingKey, value: T): T | undefined =>
      gatedReason(key) === null ? value : undefined;

    return {
      blocks,
      redirect_config: valueIfSupported('redirect_config', buildRedirectConfigForSave()),
      max_responses: valueIfSupported(
        'max_responses',
        maxResponses.trim() ? parseInt(maxResponses) : null,
      ),
      min_linkedin_connections: valueIfSupported(
        'min_linkedin_connections',
        minLinkedInConnections.trim() ? parseInt(minLinkedInConnections) : null,
      ),
      // A config or an explicit null, never undefined while supported — the toggle being off is a
      // real instruction to clear the timer, not an instruction to leave it alone.
      timer_config: valueIfSupported('timer_config', timerConfigFromForm(timerForm)),
      adaptive_probing: valueIfSupported('adaptive_probing', adaptiveProbing),
      survey_level_probing_enabled: valueIfSupported(
        'survey_level_probing_enabled',
        surveyLevelProbing,
      ),
    };
  };

  /**
   * A structural edit: the questions and their blocks changed, and nothing else did.
   *
   * These emits fire on every add/delete/duplicate/reorder and on every block edit, and on the
   * edit surfaces each one SAVES immediately. So they must not carry the settings form's values —
   * a panel the author has not opened, or a field whose prop has not finished syncing, would be
   * written as though it were their choice.
   */
  const emitStructuralChange = (
    nextQuestions: ParsedQuestion[],
    nextBlocks: SurveyBlockDefinition[],
  ) => {
    onQuestionsUpdated(nextQuestions, { ...noSettingsTouched(), blocks: nextBlocks });
  };

  // Hand the current editor contents to the parent. Every "save" control forwards the same named
  // payload, so it is built in exactly one place — a control that assembled its own could silently
  // drop a setting (quota, probing, LinkedIn gate, timer) that the others persist.
  const commitQuestionsToParent = () => {
    onQuestionsUpdated(questions, settingsFromForm());
  };

  // Single exit path for the editor's pending-edit state. The prop re-sync effect early-returns
  // while EITHER `editingQuestionId` or `hasChanges` is set, and this component is not unmounted
  // when edit mode exits (SurveyDetails only flips `readOnly`) — so a control that leaves either
  // flag standing pins the editor to its stale copy for the life of the page, and it stops
  // accepting anything the server sends back until a full reload. Three controls each having to
  // remember to reset the same flags is what left the bottom "Save Changes" desynced (#661).
  const clearPendingEditState = () => {
    setEditingQuestionId(null);
    setOriginalQuestionBeforeEdit(null);
    setHasChanges(false);
    // The caps the author ASKED for belong to the edit that is ending — see requestedSelectionCaps.
    setRequestedSelectionCaps((prev) => (Object.keys(prev).length ? {} : prev));
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Don't allow dragging while editing or in readonly mode
    if (editingQuestionId || readOnly) {
      e.preventDefault();
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set drag image opacity
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setDropTargetIndex(index);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDropTargetIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropTargetIndex(null);
      return;
    }

    // Reorder the questions array
    const reorderedQuestions = Array.from(questions);
    const [draggedQuestion] = reorderedQuestions.splice(draggedIndex, 1);
    reorderedQuestions.splice(dropIndex, 0, draggedQuestion);

    setQuestions(reorderedQuestions);
    setHasChanges(true);
    emitStructuralChange(reorderedQuestions, blocks);

    // Reset drag state
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleCreateSurvey = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // The same assembly the save controls use, so creating and editing cannot disagree about
      // what a setting is called or whether it travels.
      await onCreateSurvey(surveyName, questions, settingsFromForm());
    } catch (error) {
      console.error('Failed to create survey:', error);
    } finally {
      // `finally`, not just the catch: every parent handles its own create failures and does NOT
      // re-throw, so the catch above never ran and the button stayed on "Creating Survey…"
      // permanently — the author could read why it failed and still not retry. On success the
      // parent navigates away and this is a no-op.
      setIsSubmitting(false);
    }
  };

  // Nothing on screen yet, or a surface that is about to be replaced anyway: a spinner in place of
  // the editor costs the author nothing. On an edit surface it costs them their place.
  const editorMayBeHidden =
    SURFACES_THAT_MAY_HIDE_THE_EDITOR.includes(surface) || questions.length === 0;

  /** Regenerate/keep choice on a stale checklist. Written straight into `questions` — deliberately
   *  NOT through the edit-tracking path, since acting on the prompt isn't an authoring change.
   *  See ADR-0002 §5. */
  const applyDynamicSectionRubricChoice = useCallback(
    async (questionId: string, choice: 'regenerate' | 'keep') => {
      if (!surveyId) return;
      const call =
        choice === 'regenerate' ? regenerateDynamicSectionRubric : keepDynamicSectionRubric;
      const { section } = await call(surveyId, questionId);
      setQuestions((prev: any[]) =>
        prev.map((q) => (String(q.id) === String(questionId) ? { ...q, ...section } : q)),
      );
    },
    [surveyId, setQuestions],
  );

  if (isLoading && editorMayBeHidden) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
          <span className="text-gray-600">{UI_TEXT.LOADING.PROCESSING}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
      {/* Header - always shown for navigation */}
      <div className="mb-6">
        {/* A save in flight is SAID, not staged: the editor stays where the author left it.
            The slot is ALWAYS in the tree and only its VISIBILITY changes. Mounting it on a save
            and unmounting it after inserted and removed a line above the whole question list, and
            the block-label and rapid-fire fields save as you type — so the page jumped twice per
            character. Debouncing the save would not have fixed this: it would only have made the
            same jump happen less often. `aria-hidden` keeps the idle slot out of the a11y tree,
            so nothing is announced until a save actually starts. */}
        <div
          role="status"
          aria-live="polite"
          aria-hidden={!isLoading}
          className={`flex items-center text-gray-600 text-sm mb-4 h-5 transition-opacity ${
            isLoading ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
          {isLoading ? UI_TEXT.LOADING.SAVING : ''}
        </div>
        {!readOnly && !hideActionButtons && (
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onGoBack}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              {UI_TEXT.BUTTONS.BACK}
            </Button>
            {hasChanges && (
              <div className="flex items-center text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.UNSAVED_CHANGES}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center mb-2">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">
            {readOnly
              ? 'Questions'
              : hideActionButtons
                ? 'Edit Survey'
                : UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TITLES.REVIEW_QUESTIONS}
          </h2>
        </div>
        <p className="text-gray-600">
          {readOnly
            ? ''
            : hideActionButtons
              ? 'Edit questions, response quota, and panel provider settings for this survey.'
              : 'Review questions, configure quota, and set up panel provider.'}
        </p>
      </div>

      {/* Questions List */}
      <div className="mb-6">
        {/* Blocks are created inline via the Block dropdown on each question */}
        <div className="space-y-2 mb-4">
          {questions.map((question, index) => (
            /* `key` must stay the question ID, never `index`. No test can catch this today:
               QuestionCard holds no state, so an index key is currently inert and the whole
               survey suite passes with `key={index}` (verified, 1810/1810). The moment anyone
               adds a useState inside QuestionCard, an index key silently leaks that state to a
               different question after a delete or reorder — the author edits Q3 and Q4's
               editor opens. Deleting or reordering is exactly what this list is for. */
            <QuestionCard
              key={question.id}
              question={question}
              index={index}
              issuesByQuestion={issuesByQuestion}
              readOnly={readOnly}
              groupCount={groupCount}
              surveyId={surveyId}
              groupSurveyIds={groupSurveyIds}
              groupLabels={groupLabels}
              questions={questions}
              setQuestions={setQuestions}
              onRegenerateRubric={(qid: string) =>
                applyDynamicSectionRubricChoice(qid, 'regenerate')
              }
              onKeepRubric={(qid: string) => applyDynamicSectionRubricChoice(qid, 'keep')}
              setRequestedSelectionCaps={setRequestedSelectionCaps}
              blocks={blocks}
              setBlocks={setBlocks}
              editingQuestionId={editingQuestionId}
              setEditingQuestionId={setEditingQuestionId}
              originalQuestionBeforeEdit={originalQuestionBeforeEdit}
              setOriginalQuestionBeforeEdit={setOriginalQuestionBeforeEdit}
              editingBlockId={editingBlockId}
              setEditingBlockId={setEditingBlockId}
              setEditingBlockConditionId={setEditingBlockConditionId}
              editingBlockConditionId={editingBlockConditionId}
              setHasChanges={setHasChanges}
              previewPickerFor={previewPickerFor}
              setPreviewPickerFor={setPreviewPickerFor}
              draggedIndex={draggedIndex}
              dropTargetIndex={dropTargetIndex}
              setExpandedQuestionMedia={setExpandedQuestionMedia}
              expandedQuestionMedia={expandedQuestionMedia}
              setExpandedOptionMedia={setExpandedOptionMedia}
              expandedOptionMedia={expandedOptionMedia}
              randomizationPoolsInSurvey={randomizationPoolsInSurvey}
              focusQuestionForEdit={focusQuestionForEdit}
              openPreview={openPreview}
              renderVideoThumb={renderVideoThumb}
              handlePreviewClick={handlePreviewClick}
              updateQuestion={updateQuestion}
              editorSelectionCap={editorSelectionCap}
              boundedSelectionCapNote={boundedSelectionCapNote}
              repairedSelectionCapNote={repairedSelectionCapNote}
              updateQuestionOptions={updateQuestionOptions}
              changeQuestionType={changeQuestionType}
              removeQuestion={removeQuestion}
              insertQuestionAt={insertQuestionAt}
              duplicateQuestion={duplicateQuestion}
              getBlockLabel={getBlockLabel}
              duplicateBlock={duplicateBlock}
              updateBlockRandomizeWithin={updateBlockRandomizeWithin}
              updateBlockDisplayCondition={updateBlockDisplayCondition}
              updateBlockLabel={updateBlockLabel}
              updateBlockRapidFire={updateBlockRapidFire}
              addChoice={addChoice}
              removeChoice={removeChoice}
              updateChoice={updateChoice}
              addQuestionMedia={addQuestionMedia}
              removeQuestionMedia={removeQuestionMedia}
              replaceQuestionMedia={replaceQuestionMedia}
              reorderQuestionMedia={reorderQuestionMedia}
              addOptionMedia={addOptionMedia}
              removeOptionMedia={removeOptionMedia}
              replaceOptionMedia={replaceOptionMedia}
              reorderOptionMedia={reorderOptionMedia}
              getOptionMediaUrls={getOptionMediaUrls}
              getOptionGroupMediaUrls={getOptionGroupMediaUrls}
              countGroupMedia={countGroupMedia}
              addOptionGroupMedia={addOptionGroupMedia}
              removeOptionGroupMedia={removeOptionGroupMedia}
              replaceOptionGroupMedia={replaceOptionGroupMedia}
              updateChoiceAction={updateChoiceAction}
              getChoiceAction={getChoiceAction}
              addMatrixItem={addMatrixItem}
              removeMatrixItem={removeMatrixItem}
              updateMatrixItem={updateMatrixItem}
              addConjointAttribute={addConjointAttribute}
              removeConjointAttribute={removeConjointAttribute}
              updateConjointAttribute={updateConjointAttribute}
              getQuestionTypeLabel={getQuestionTypeLabel}
              hasChoices={hasChoices}
              hasScale={hasScale}
              hasRanking={hasRanking}
              hasMatrix={hasMatrix}
              isMultiSelect={isMultiSelect}
              hasMaxDiff={hasMaxDiff}
              hasConjoint={hasConjoint}
              hasImageHotspot={hasImageHotspot}
              hasAllocation={hasAllocation}
              hasPhotoUpload={hasPhotoUpload}
              redirectConfigInvalid={redirectConfigInvalid}
              commitQuestionsToParent={commitQuestionsToParent}
              clearPendingEditState={clearPendingEditState}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
            />
          ))}
        </div>

        {/* Add New Question Button - only visible when not in readonly mode and not editing */}
        {!readOnly && editingQuestionId === null && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={addNewQuestion}
              className="text-blue-600 hover:text-blue-700 border-blue-300"
            >
              + Add Question
            </Button>
          </div>
        )}
      </div>

      {/* Between-subject groups — fixed at creation, so shown rather than offered. Not gated on
          `readOnly`: it is disabled either way, and an author looking for it should find it in both
          the review view and the editor. */}
      {lockedReason('group_count', effectiveSurface) && (
        <LockedSettingControl
          settingKey="group_count"
          title={UI_TEXT.HUMAN_SURVEY.LABELS.GROUP_COUNT}
          value={groupCount}
          reason={lockedReason('group_count', effectiveSurface) as string}
        />
      )}

      {/* Response Quota Configuration - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Response Quota</h3>
            <div className="text-sm font-medium text-gray-700 mb-1">Maximum Responses</div>
            <div className="text-sm text-gray-900">{maxResponses || 'Unlimited'}</div>
          </div>
        </div>
      )}

      {/* Response Quota Configuration - Edit Mode */}
      {!readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowQuotaConfig(!showQuotaConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900">Response Quota (Optional)</h3>
            </div>
            <span className="text-gray-400">{showQuotaConfig ? '−' : '+'}</span>
          </button>

          {showQuotaConfig && (
            <div className="px-4 pb-4 space-y-4">
              <div data-setting-key="max_responses">
                <label
                  htmlFor="maxResponses"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Maximum Responses
                </label>
                <input
                  type="number"
                  id="maxResponses"
                  value={maxResponses}
                  onChange={(e) => setMaxResponses(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g., 100"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Set a maximum number of responses for this survey. Leave empty for unlimited
                  responses. When the quota is reached, new participants will be redirected (if
                  redirect config is set).
                </p>
              </div>

              {/* Hidden on every surface via LINKEDIN_GATE_TEMPORARILY_OFF in
                  surveyAuthoringSettings.ts — the author gets the notice, not a control that would
                  configure a gate the backend drops. */}
              {gatedReason('min_linkedin_connections') ? (
                <GatedSettingNotice
                  settingKey="min_linkedin_connections"
                  title="Minimum LinkedIn Connections"
                  reason={gatedReason('min_linkedin_connections') as string}
                />
              ) : (
                <div data-setting-key="min_linkedin_connections">
                  <label
                    htmlFor="minLinkedInConnections"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <span className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                      Minimum LinkedIn Connections
                    </span>
                  </label>
                  <input
                    type="number"
                    id="minLinkedInConnections"
                    value={minLinkedInConnections}
                    onChange={(e) => setMinLinkedInConnections(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="e.g., 50"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Require respondents to have a minimum number of LinkedIn connections. When set,
                    a special LinkedIn verification link will be available. Leave empty to disable
                    LinkedIn verification.
                  </p>
                </div>
              )}

              {/* Survey-wide countdown (spec 35). Duration and mode stay mounted but disabled
                  while the timer is off, rather than unmounting: a researcher toggling it on
                  should see what they are about to set, not an empty space that then jumps. */}
              {gatedReason('timer_config') ? (
                <GatedSettingNotice
                  settingKey="timer_config"
                  title={UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.SECTION_TITLE}
                  reason={gatedReason('timer_config') as string}
                />
              ) : (
                <div className="pt-4 border-t border-gray-200" data-setting-key="timer_config">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      id="timerEnabled"
                      checked={timerForm.enabled}
                      onChange={(e) => setTimerForm({ ...timerForm, enabled: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.ENABLE_LABEL}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.ENABLE_HELP}
                  </p>

                  <div className={`ml-6 mt-3 space-y-3 ${timerForm.enabled ? '' : 'opacity-50'}`}>
                    <div>
                      <label
                        htmlFor="timerDuration"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.DURATION_LABEL}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          id="timerDuration"
                          value={timerForm.durationMinutes}
                          disabled={!timerForm.enabled}
                          onChange={(e) =>
                            setTimerForm({ ...timerForm, durationMinutes: e.target.value })
                          }
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50"
                          placeholder={
                            UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.DURATION_PLACEHOLDER
                          }
                          min={MIN_DURATION_MINUTES}
                          max={MAX_DURATION_MINUTES}
                        />
                        <span className="text-sm text-gray-600">
                          {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.DURATION_SUFFIX}
                        </span>
                      </div>
                      {durationWarning(timerForm) && (
                        <p className="text-xs text-amber-700 mt-1">{durationWarning(timerForm)}</p>
                      )}
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-1">
                        {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.MODE_LABEL}
                      </span>
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="timerMode"
                          value="guidance"
                          checked={timerForm.mode === 'guidance'}
                          disabled={!timerForm.enabled}
                          onChange={() => setTimerForm({ ...timerForm, mode: 'guidance' })}
                          className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.MODE_GUIDANCE}
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-1">
                        <input
                          type="radio"
                          name="timerMode"
                          value="hard_stop"
                          checked={timerForm.mode === 'hard_stop'}
                          disabled={!timerForm.enabled}
                          onChange={() => setTimerForm({ ...timerForm, mode: 'hard_stop' })}
                          className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.MODE_HARD_STOP}
                      </label>
                      {timerForm.enabled && timerForm.mode === 'hard_stop' && (
                        <p className="text-xs text-amber-700 mt-1">
                          {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.TIMER.MODE_HARD_STOP_HELP}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Survey-level probing - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Survey-level probing</h3>
            <div className="text-sm text-gray-900">
              {surveyLevelProbing ? 'Enabled' : 'Disabled'}
            </div>
            <p className="text-xs text-gray-600 mt-2">{SURVEY_LEVEL_PROBING_DETAIL}</p>
          </div>
        </div>
      )}

      {/* Survey-level probing - Edit Mode. Independent of the adaptive switch: this one controls
          whether the follow-up decision reads the WHOLE session transcript (and skips a probe the
          respondent already answered under an earlier question), or judges each question on its
          own answer alone. On by default.

          Gated on the surface: the flag rides the questions PUT, and the create endpoints carry no
          ai_features — rendering it in the new-survey builder would silently discard an opt-out. It
          is set in the editor once the survey (or group) exists, and where it is NOT available the
          author is told so rather than finding an empty space (#701). */}
      {!readOnly && gatedReason('survey_level_probing_enabled') && (
        <GatedSettingNotice
          settingKey="survey_level_probing_enabled"
          title="Survey-level probing"
          reason={gatedReason('survey_level_probing_enabled') as string}
        />
      )}
      {!readOnly && !gatedReason('survey_level_probing_enabled') && (
        <div
          className="mb-6 border border-gray-200 rounded-lg"
          data-setting-key="survey_level_probing_enabled"
        >
          <div className="p-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300"
                checked={surveyLevelProbing}
                onChange={(e) => setSurveyLevelProbing(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Enable survey-level probing
                </span>
                <span className="block text-xs text-gray-500 mt-1">
                  {SURVEY_LEVEL_PROBING_DETAIL}
                </span>
                <span className="block text-xs text-gray-500 mt-1">
                  Turn this OFF to probe every question independently — each follow-up is decided
                  only from that question's own answer, so probes fire consistently across
                  respondents instead of thinning out later in the survey.
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Adaptive features - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Adaptive features</h3>
            <div className="text-sm text-gray-900">{adaptiveProbing ? 'Enabled' : 'Disabled'}</div>
            {adaptiveProbing && (
              <ul className="mt-3 space-y-1.5">
                {ADAPTIVE_FEATURE_BULLETS.map((b) => (
                  <li key={b.title} className="text-xs text-gray-600">
                    <span className="font-medium text-gray-800">{b.title}</span> — {b.detail}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Adaptive features - Edit Mode. One switch for the whole adaptive pipeline: rubric-driven
          follow-up probing, cross-question coverage (don't re-ask what was answered elsewhere), and
          adaptive question scoping (narrow an upcoming question to its still-uncovered points).

          Gated exactly like survey-level probing, and for the same reason: this is an ai_features
          flag, the create request carries none, so a switch on the create form was a control that
          did nothing (#701). Where it is unavailable the author is told why. */}
      {!readOnly && gatedReason('adaptive_probing') && (
        <GatedSettingNotice
          settingKey="adaptive_probing"
          title="Adaptive features"
          reason={gatedReason('adaptive_probing') as string}
        />
      )}
      {!readOnly && !gatedReason('adaptive_probing') && (
        <div className="mb-6 border border-gray-200 rounded-lg" data-setting-key="adaptive_probing">
          <div className="p-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300"
                checked={adaptiveProbing}
                onChange={(e) => setAdaptiveProbing(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Enable adaptive features
                </span>
                <span className="block text-xs text-gray-500 mt-1">
                  One switch for the AI's adaptive interviewing. Off by default. Turning it on adds:
                </span>
                <ul className="mt-2 space-y-1.5">
                  {ADAPTIVE_FEATURE_BULLETS.map((b) => (
                    <li key={b.title} className="text-xs text-gray-600">
                      <span className="font-medium text-gray-800">{b.title}</span> — {b.detail}
                    </li>
                  ))}
                </ul>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Experimental: Conversational / discussion-guide survey style */}
      <div className="mb-6 border border-gray-200 rounded-lg">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900">Survey style</h3>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#07E5D1] bg-[#E8FCFA] rounded px-2 py-0.5">
              Experimental
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Traditional runs your fixed questions one at a time. Conversational hands the interview
            to an AI voice moderator that follows a discussion guide — it asks adaptively, probes,
            and skips what's already covered.
          </p>

          {readOnly ? (
            <div className="text-sm text-gray-900 mb-2">
              {surveyStyle === 'conversational' ? 'Conversational (AI moderator)' : 'Traditional'}
            </div>
          ) : (
            <div className="flex gap-4 mb-3">
              {(['traditional', 'conversational'] as SurveyStyle[]).map((style) => (
                <label key={style} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="surveyStyle"
                    checked={surveyStyle === style}
                    onChange={() => setSurveyStyle(style)}
                    className="border-gray-300"
                  />
                  <span className="text-gray-900">
                    {style === 'conversational' ? 'Conversational (AI moderator)' : 'Traditional'}
                  </span>
                </label>
              ))}
            </div>
          )}

          {surveyStyle === 'conversational' && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {!readOnly && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3">
                  In conversational mode the fixed questions above are not shown to respondents —
                  the moderator runs the discussion guide below instead.
                </p>
              )}
              <DiscussionGuideEditor
                value={discussionGuide}
                onChange={setDiscussionGuide}
                readOnly={readOnly}
              />
            </div>
          )}

          {!readOnly && (
            <div className="mt-4 flex items-center gap-3">
              {surveyId ? (
                <>
                  <button
                    type="button"
                    onClick={handleSaveConversationalSettings}
                    disabled={convSaveState === 'saving'}
                    className="bg-[#1C1C1E] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#374151] transition-colors disabled:opacity-50"
                  >
                    {convSaveState === 'saving' ? 'Saving…' : 'Save survey style'}
                  </button>
                  {convSaveState === 'saved' && (
                    <span className="text-xs text-emerald-600">Saved ✓</span>
                  )}
                  {convSaveState === 'error' && (
                    <span className="text-xs text-red-600">
                      {convSaveError || 'Failed to save'}
                    </span>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500">
                  Save the survey first, then configure the conversational guide here.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Panel Provider Redirect Configuration - Read Only */}
      {readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg">
          <div className="p-4 space-y-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Panel Provider Integration
            </h3>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Redirect Base URL</div>
              <div className="text-sm text-gray-900">{redirectBaseUrl || 'Not configured'}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Screen Out URL</div>
              <div className="text-sm text-gray-900">
                {screenedOutUrl || 'Not configured (uses base URL)'}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Tracking Parameter Name</div>
              <div className="text-sm text-gray-900">{trackingParamName || 'Not configured'}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Completion Parameters</div>
              <div className="text-sm text-gray-900">{completionParams || 'Not configured'}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Screen Out Parameters</div>
              <div className="text-sm text-gray-900">{screenOutParams || 'Not configured'}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Quota Full Parameters</div>
              <div className="text-sm text-gray-900">{quotaFullParams || 'Not configured'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Panel Provider Redirect Configuration - Edit Mode */}
      {!readOnly && (
        <div className="mb-6 border border-gray-200 rounded-lg" data-setting-key="redirect_config">
          <button
            type="button"
            onClick={() => setShowRedirectConfig(!showRedirectConfig)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-900">
                Panel Provider Integration (Optional)
              </h3>
            </div>
            <span className="text-gray-400">{showRedirectConfig ? '−' : '+'}</span>
          </button>

          {showRedirectConfig && (
            <div className="p-4 pt-0 space-y-4">
              <p className="text-xs text-gray-600 mb-2">
                Choose what respondents see after they finish the survey.
              </p>

              {/* Redirect mode: off (internal thank-you page) vs on (external panel) */}
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="redirect-mode"
                    className="mt-1"
                    checked={!redirectEnabled}
                    onChange={() => setRedirectEnabled(false)}
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Show thank-you page</span> — respondents see a
                    completion message, no external redirect. Recommended unless you distribute
                    through a panel that needs respondents sent back.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="redirect-mode"
                    className="mt-1"
                    checked={redirectEnabled}
                    onChange={() => setRedirectEnabled(true)}
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Redirect to an external panel</span> — send
                    respondents to a panel provider URL after completion.
                  </span>
                </label>
              </div>

              {redirectEnabled && (
                <>
                  {/* Provider Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Panel Provider
                    </label>
                    <select
                      value={selectedProvider}
                      onChange={(e) => handleProviderChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Select Provider...</option>
                      <option value="userinterviews">User Interviews</option>
                      <option value="dynata">Dynata</option>
                      <option value="custom">Custom</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select a provider to auto-fill configuration fields with recommended values
                    </p>
                  </div>

                  {/* Redirect Base URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Redirect Base URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={redirectBaseUrl}
                      onChange={(e) => setRedirectBaseUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="https://example.com/survey/complete"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Base URL for redirecting participants (required if using panel provider
                      integration)
                    </p>
                    {redirectConfigInvalid && (
                      <p className="text-xs text-red-600 mt-1">
                        Enter a redirect URL, or switch to "Show thank-you page" to disable
                        redirects.
                      </p>
                    )}
                  </div>

                  {/* Screen Out URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Screen Out URL
                    </label>
                    <input
                      type="url"
                      value={screenedOutUrl}
                      onChange={(e) => setScreenedOutUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="https://example.com/survey/incomplete"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate URL for screened out participants (optional, falls back to base URL
                      if not set)
                    </p>
                  </div>

                  {/* Tracking Parameter Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tracking Parameter Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={trackingParamName}
                      onChange={(e) => setTrackingParamName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="iid (for UserInterviews) or psid (for Dynata)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Parameter name for participant tracking ID (e.g., "iid", "psid")
                    </p>
                  </div>

                  {/* Completion Parameters */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Completion Parameters
                    </label>
                    <input
                      type="text"
                      value={completionParams}
                      onChange={(e) => setCompletionParams(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="rst=1, basic=YYYY"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Additional parameters for completion redirect (optional)
                    </p>
                  </div>

                  {/* Screen Out Parameters */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Screen Out Parameters
                    </label>
                    <input
                      type="text"
                      value={screenOutParams}
                      onChange={(e) => setScreenOutParams(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="rst=2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Parameters for screen-out redirect (optional)
                    </p>
                  </div>

                  {/* Quota Full Parameters */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quota Full Parameters
                    </label>
                    <input
                      type="text"
                      value={quotaFullParams}
                      onChange={(e) => setQuotaFullParams(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="rst=3"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Parameters for quota-full redirect (optional)
                    </p>
                  </div>

                  {/* Example Configuration */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <p className="text-xs font-medium text-blue-900 mb-2">Example Configuration:</p>
                    <div className="text-xs text-blue-800 space-y-1">
                      <p>
                        <strong>UserInterviews:</strong>
                      </p>
                      <p>
                        • Base URL:
                        https://www.userinterviews.com/project-task-events/custom_link_with_redirect/complete
                      </p>
                      <p>
                        • Screen Out URL:
                        https://www.userinterviews.com/project-task-events/custom_link_with_redirect/incomplete
                      </p>
                      <p>• Tracking Param: iid</p>
                      <p className="mt-2">
                        <strong>Dynata:</strong>
                      </p>
                      <p>• Base URL: https://dkr1.ssisurveys.com/projects/end</p>
                      <p>• Tracking Param: psid</p>
                      <p>• Completion Params: rst=1, basic=YYYY</p>
                      <p>• Screen Out Params: rst=2</p>
                      <p>• Quota Full Params: rst=3</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Save Changes Button - only in edit mode for existing surveys */}
      {!readOnly && hideActionButtons && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={() => {
              // Save all changes and exit edit mode. Clearing the pending-edit flags is what
              // lets the editor accept server updates again after this — see clearPendingEditState.
              commitQuestionsToParent();
              clearPendingEditState();
              onGoBack(); // Exit edit mode
            }}
            icon={<Save className="w-4 h-4" />}
            disabled={isLoading || redirectConfigInvalid}
          >
            Save Changes
          </Button>
        </div>
      )}

      {/* Actions */}
      {!readOnly && !hideActionButtons && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.QUESTIONS_COUNT(questions.length)}
          </p>

          <div className="flex space-x-3">
            <Button variant="outline" onClick={onGoBack}>
              {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.RE_ENTER_QUESTIONS}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateSurvey}
              disabled={editingQuestionId !== null || isSubmitting || redirectConfigInvalid}
            >
              {isSubmitting
                ? UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CREATING_SURVEY
                : UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CREATE_SURVEY}
            </Button>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {videoPreviewUrl && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setVideoPreviewUrl(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-8 pointer-events-none">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-2xl w-full pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">Video Preview</span>
                <button
                  type="button"
                  onClick={() => setVideoPreviewUrl(null)}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100"
                >
                  <span className="text-gray-500 text-lg">&times;</span>
                </button>
              </div>
              <div className="aspect-video">
                {isExternalStreamingVideoUrl(videoPreviewUrl) ? (
                  <iframe
                    src={videoPreviewUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={videoPreviewUrl}
                    controls
                    autoPlay
                    className="w-full h-full bg-black"
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SurveyQuestionsPreview;
